import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { relativeTime, formatLima } from '../lib/utils'
import {
  Search, RefreshCw, User, Phone, Calendar, Hash,
  MessageSquareText, Loader2, Bot, MessageCircle, Globe,
} from 'lucide-react'

/* ── Utilidades ──────────────────────────────────────────────────────────── */
function displayName(c) {
  return c.display_name?.trim() || c.phone?.trim() || c.user_id
}

/* ── ChannelBadge ────────────────────────────────────────────────────────── */
function ChannelBadge({ channel, size = 14, withLabel = false }) {
  const isWa  = channel === 'whatsapp'
  const Icon  = isWa ? MessageCircle : Globe
  const color = isWa ? 'var(--teal-l)' : 'var(--grana-4)'
  const bg    = isWa ? 'rgba(46,150,171,.12)' : 'rgba(193,39,45,.12)'
  const ring  = isWa ? '1px solid rgba(46,150,171,.25)' : '1px solid rgba(193,39,45,.25)'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: size + 8, height: size + 8, borderRadius: 6,
        background: bg, outline: ring,
      }}>
        <Icon size={size} style={{ color }} />
      </span>
      {withLabel && (
        <span style={{ fontSize: 12, fontWeight: 500, color }}>
          {isWa ? 'WhatsApp' : 'Web'}
        </span>
      )}
    </span>
  )
}

const FILTERS = [
  { key: 'all',       label: 'Todos'     },
  { key: 'whatsapp',  label: 'WhatsApp'  },
  { key: 'web',       label: 'Web'       },
]

/* ── Página principal ────────────────────────────────────────────────────── */
export default function Chatbot() {
  const [contacts,      setContacts]      = useState([])
  const [loading,       setLoading]       = useState(true)
  const [filter,        setFilter]        = useState('all')
  const [search,        setSearch]        = useState('')
  const [selected,      setSelected]      = useState(null)
  const [messages,      setMessages]      = useState([])
  const [loadingThread, setLoadingThread] = useState(false)
  const threadRef = useRef(null)

  /* Cargar lista de conversaciones */
  const loadContacts = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('conversation_list')
      .select('*')
      .order('last_message_time', { ascending: false })
      .limit(500)
    setContacts(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { loadContacts() }, [loadContacts])

  /* Abrir conversación */
  const openConversation = useCallback(async (c) => {
    setSelected(c)
    setLoadingThread(true)
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('user_id', c.user_id)     // user_id de la vista conversation_list
      .order('time', { ascending: true })
      .limit(1000)
    setMessages(data || [])
    setLoadingThread(false)
    requestAnimationFrame(() => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight
    })
  }, [])

  /* Realtime */
  useEffect(() => {
    const ch = supabase.channel('chatbot-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, () => {
        loadContacts()
        if (selected) openConversation(selected)
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contacts' }, loadContacts)
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [selected, loadContacts, openConversation])

  /* Filtrado local */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return contacts.filter(c => {
      if (filter !== 'all' && c.channel !== filter) return false
      if (!q) return true
      return displayName(c).toLowerCase().includes(q) ||
             (c.last_message || '').toLowerCase().includes(q)
    })
  }, [contacts, filter, search])

  const counts = useMemo(() => ({
    all:      contacts.length,
    whatsapp: contacts.filter(c => c.channel === 'whatsapp').length,
    web:      contacts.filter(c => c.channel === 'web').length,
  }), [contacts])

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', height: '100vh' }}>

      {/* ── Panel izquierdo: lista ─────────────────────────────────────────── */}
      <section style={{
        width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column',
        borderRight: '1px solid rgba(30,38,48,.7)',
        background: 'rgba(14,19,24,.4)',
      }}>
        <header style={{ borderBottom: '1px solid rgba(30,38,48,.7)', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h1 className="font-display" style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>
              Conversaciones
            </h1>
            <button onClick={loadContacts} title="Actualizar" style={{
              padding: 6, borderRadius: 6, color: 'var(--ash-500)',
              background: 'transparent', transition: 'all .15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(30,38,48,.6)'; e.currentTarget.style.color = 'var(--teal-l)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ash-500)' }}>
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 12, top: 10, color: 'var(--ash-600)', pointerEvents: 'none' }} />
            <input className="input" placeholder="Buscar nombre o mensaje…"
              style={{ paddingLeft: 36, paddingTop: 8, paddingBottom: 8, fontSize: 13 }}
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          {/* Filtros */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`chip ${filter === f.key ? 'chip-active' : ''}`}>
                {f.label}
                <span style={{ color: 'var(--ash-600)' }}>{counts[f.key]}</span>
              </button>
            ))}
          </div>
        </header>

        {/* Lista */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160, color: 'var(--ash-600)' }}>
              <Loader2 className="animate-spin" size={22} />
            </div>
          ) : filtered.length === 0 ? (
            <p style={{ padding: '40px 16px', textAlign: 'center', fontSize: 13, color: 'var(--ash-600)' }}>
              Sin conversaciones para este filtro.
            </p>
          ) : filtered.map(c => {
            const active = selected?.user_id === c.user_id
            return (
              <button key={c.user_id} onClick={() => openConversation(c)} style={{
                display: 'flex', width: '100%', alignItems: 'flex-start', gap: 12,
                padding: '12px 16px', textAlign: 'left',
                borderBottom: '1px solid rgba(30,38,48,.4)',
                background: active ? 'rgba(46,150,171,.1)' : 'transparent',
                transition: 'background .1s',
              }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(22,28,36,.6)' }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}>

                {/* Avatar + badge canal */}
                <div style={{ position: 'relative', marginTop: 2, flexShrink: 0 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'var(--ink-700)', color: 'var(--ash-400)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <User size={17} />
                  </div>
                  <span style={{ position: 'absolute', bottom: -4, right: -4 }}>
                    <ChannelBadge channel={c.channel} size={10} />
                  </span>
                </div>

                {/* Texto */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayName(c)}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--ash-600)', flexShrink: 0 }}>
                      {relativeTime(c.last_message_time)}
                    </span>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--ash-500)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.last_message || '—'}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {/* ── Panel central: hilo ────────────────────────────────────────────── */}
      <section style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!selected ? (
          <div style={{ display: 'grid', placeItems: 'center', height: '100%', textAlign: 'center' }}>
            <div>
              <MessageSquareText size={40} style={{ color: 'var(--ink-600)', margin: '0 auto' }} />
              <p style={{ marginTop: 16, fontSize: 13, color: 'var(--ash-500)' }}>
                Selecciona una conversación para verla
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Header hilo */}
            <header style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 20px', borderBottom: '1px solid rgba(30,38,48,.7)',
              flexShrink: 0,
            }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--ink-700)', color: 'var(--ash-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <User size={17} />
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayName(selected)}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                  <ChannelBadge channel={selected.channel} size={11} withLabel />
                </div>
              </div>
            </header>

            {/* Mensajes */}
            <div ref={threadRef} style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {loadingThread ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--ash-600)' }}>
                  <Loader2 className="animate-spin" size={22} />
                </div>
              ) : messages.map((m, i) => {
                const isBot = m.role === 'assistant'
                return (
                  <div key={m.id ?? i} style={{ display: 'flex', justifyContent: isBot ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '72%', borderRadius: 16, padding: '10px 16px', fontSize: 13, lineHeight: 1.55,
                      background: isBot ? 'rgba(46,150,171,.15)' : 'var(--ink-800)',
                      color: isBot ? '#e8f6f9' : 'var(--ash-200, #C7CCD3)',
                      outline: isBot ? '1px solid rgba(46,150,171,.2)' : '1px solid rgba(30,38,48,.6)',
                    }}>
                      {/* Etiqueta Bot/Cliente */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', opacity: .6, marginBottom: 4 }}>
                        {isBot ? <><Bot size={10} /> Bot</> : <><User size={10} /> Cliente</>}
                      </div>
                      <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-words' }}>{m.content}</p>
                      <p style={{ marginTop: 4, textAlign: 'right', fontSize: 10, opacity: .5 }}>
                        {formatLima(m.time)}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </section>

      {/* ── Panel derecho: detalle ─────────────────────────────────────────── */}
      {selected && (
        <aside style={{
          width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderLeft: '1px solid rgba(30,38,48,.7)',
          background: 'rgba(14,19,24,.4)', padding: 20,
        }}>
          {/* Avatar + nombre */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--ink-700)', color: 'var(--ash-400)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={28} />
            </div>
            <p className="font-display" style={{ marginTop: 12, fontSize: 15, fontWeight: 600, color: '#fff' }}>
              {displayName(selected)}
            </p>
            <div style={{ marginTop: 8 }}>
              <ChannelBadge channel={selected.channel} size={13} withLabel />
            </div>
          </div>

          {/* Datos */}
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Detail Icon={Phone}          label="Identificador / teléfono" value={selected.phone || selected.user_id} />
            <Detail Icon={Hash}           label="Mensajes"                 value={String(selected.message_count ?? 0)} />
            <Detail Icon={Calendar}       label="Primer contacto"          value={selected.first_seen ? formatLima(selected.first_seen) : '—'} />
            <Detail Icon={Calendar}       label="Última actividad"         value={selected.last_message_time ? formatLima(selected.last_message_time) : '—'} />
            <Detail Icon={MessageSquareText} label="Canal"                 value={selected.channel === 'whatsapp' ? 'WhatsApp' : 'Web'} />
          </div>
        </aside>
      )}
    </div>
  )
}

function Detail({ Icon, label, value }) {
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ash-600)', marginBottom: 4 }}>
        <Icon size={12} /> {label}
      </p>
      <p style={{ fontSize: 13, color: 'var(--ash-300)', wordBreak: 'break-words' }}>{value}</p>
    </div>
  )
}
